'use client';

/**
 * ConsolidatedNav — auto-rendered horizontal tab bar.
 *
 * Shows up at the top of the main content area when the user is on a route
 * that belongs to a feature group whose sub-features were de-duplicated
 * out of the sidebar. Lets the user jump between related views in one
 * click without us having to add multiple sidebar entries.
 *
 * Groups (extend by adding more entries below):
 *   - /seo   → Auto-SEO Engine, AI Site Scan
 *
 * De-duplicated: the standalone "AI Site Auditor" duplicated "AI Site Scan";
 * "Instant Indexing" and "Internal Links" already live as tabs INSIDE the
 * Auto-SEO Engine (the "Google Instant Indexer & Pinger" and "Internal Link
 * Visualizer & Schema" sub-tabs), so those three were removed here to avoid
 * showing the same feature twice and confusing users.
 *
 * On routes outside any group, the bar renders nothing (returns null).
 */

import { usePathname } from 'next/navigation';
import Link from 'next/link';

// 2026-09-08: the SEO group (Auto-SEO Engine / AI Site Scan) was removed with those features.
const GROUPS = [];

function matchGroup(pathname) {
  for (const g of GROUPS) {
    if (g.tabs.some(t => pathname === t.href || pathname.startsWith(t.href + '/'))) {
      return g;
    }
  }
  return null;
}

export default function ConsolidatedNav() {
  const pathname = usePathname() || '';
  const group = matchGroup(pathname);
  if (!group) return null;

  return (
    <nav
      aria-label="Related views"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        padding: '14px 20px',
        marginBottom: 8,
        borderBottom: '1px solid rgba(15,23,42,0.05)',
        background: 'rgba(15,23,42,0.015)',
      }}
    >
      {group.tabs.map(t => {
        const active = pathname === t.href || pathname.startsWith(t.href + '/');
        const rgb = hexToRgb(group.accent);
        return (
          <Link
            key={t.href}
            href={t.href}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 14px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: active ? 700 : 500,
              color: active ? group.accent : '#475569',
              textDecoration: 'none',
              background: active
                ? `rgba(${rgb}, 0.12)`
                : 'rgba(15,23,42,0.02)',
              border: `1px solid ${active ? `rgba(${rgb}, 0.4)` : 'rgba(15,23,42,0.05)'}`,
              transition: 'all 0.18s ease',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ fontSize: 15 }}>{t.icon}</span>
            <span>{t.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

function hexToRgb(hex) {
  const v = hex.replace('#', '');
  const r = parseInt(v.substring(0, 2), 16);
  const g = parseInt(v.substring(2, 4), 16);
  const b = parseInt(v.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}
